import React, { useState, useEffect } from 'react'
import { analytics, type AnalyticsEventData } from '../services/analytics'
import PasswordAuth from '../components/PasswordAuth'
import './AdminDashboard.css'

interface DashboardStats {
  visitorsCount: number
  playersCount: number
  play3MinCount: number
  completersCount: number
  surveyRespondentsCount: number
  totalEvents: number
  uniqueSessions: number
  completionRate: number
  surveyResponseRate: number
  errorRate: number
  events: (AnalyticsEventData & { id: string })[]
}

interface SurveyBreakdown {
  excellent: number
  good: number
  fair: number
  poor: number
  total: number
}

interface ErrorBreakdown {
  network: number
  device: number
  hls: number
  session: number
  timeout: number
  general: number
  total: number
}

// Mock data generator removed for better performance

const AdminDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [surveyBreakdown, setSurveyBreakdown] = useState<SurveyBreakdown | null>(null)
  const [, ] = useState<ErrorBreakdown | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [timeRange, setTimeRange] = useState(-1) // -1 = All time
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData()

      let interval: NodeJS.Timeout | null = null
      if (autoRefresh) {
        interval = setInterval(loadDashboardData, 30000) // 30 seconds
      }

      return () => {
        if (interval) clearInterval(interval)
      }
    }
  }, [isAuthenticated, autoRefresh, timeRange])

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      
      // Get analytics data - try Firebase first
      let dashboardStats
      try {
        console.log('🚀 Starting dashboard data load at:', new Date().toISOString())
        const startTime = Date.now()
        dashboardStats = await analytics.getSessionStats(timeRange) as any
        const endTime = Date.now()
        console.log(`✅ Dashboard data loaded in ${endTime - startTime}ms:`, dashboardStats)
      } catch (error) {
        console.error('❌ Failed to fetch Firebase data:', error)
        // Use mock data for demonstration
        dashboardStats = {
          visitorsCount: 0,
          playersCount: 0,
          play3MinCount: 0,
          completersCount: 0,
          surveyRespondentsCount: 0,
          totalEvents: 0,
          uniqueSessions: 0,
          completionRate: 0,
          surveyResponseRate: 0,
          errorRate: 0,
          events: [] // Empty events for better performance
        }
        console.warn('Using mock data as fallback')
      }
      setStats(dashboardStats)
      
      // Calculate survey breakdown
      const surveyEvents = dashboardStats.events.filter((e: any) => e.eventType === 'survey_completed')
      // Survey event logging removed for performance
      
      const surveyData: SurveyBreakdown = {
        excellent: surveyEvents.filter((e: any) => e.surveyRating === 'excellent').length,
        good: surveyEvents.filter((e: any) => e.surveyRating === 'good').length,
        fair: surveyEvents.filter((e: any) => e.surveyRating === 'fair').length,
        poor: surveyEvents.filter((e: any) => e.surveyRating === 'poor').length,
        total: surveyEvents.length
      }
      
      // Survey breakdown logging removed for performance
      setSurveyBreakdown(surveyData)
      
      setLastUpdated(new Date())
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatPercentage = (value: number): string => {
    return `${Math.round(value * 100) / 100}%`
  }

  const getSurveyPercentage = (count: number, total: number): number => {
    return total > 0 ? (count / total) * 100 : 0
  }

  // デバイス別の集計を計算する関数
  const getDeviceBreakdown = (events: (AnalyticsEventData & { id: string })[], eventType: string) => {
    const filteredEvents = events.filter(e => e.eventType === eventType)
    const uniqueSessions = new Set()
    const deviceCounts = { mobile: 0, desktop: 0 }
    
    filteredEvents.forEach(event => {
      if (!uniqueSessions.has(event.sessionId)) {
        uniqueSessions.add(event.sessionId)
        const deviceType = event.deviceType
        if (deviceType === 'mobile' || deviceType === 'tablet') {
          deviceCounts.mobile++
        } else if (deviceType === 'desktop') {
          deviceCounts.desktop++
        }
      }
    })
    
    return deviceCounts
  }

  // デバイス別内訳の表示文字列を生成
  const formatDeviceBreakdown = (breakdown: {mobile: number, desktop: number}) => {
    const total = breakdown.mobile + breakdown.desktop
    const mobilePercent = total > 0 ? ((breakdown.mobile / total) * 100).toFixed(1) : '0.0'
    const desktopPercent = total > 0 ? ((breakdown.desktop / total) * 100).toFixed(1) : '0.0'
    
    return (
      <>
        モバイル: {breakdown.mobile} ({mobilePercent}%)<br />
        PC: {breakdown.desktop} ({desktopPercent}%)
      </>
    )
  }


  if (!isAuthenticated) {
    return (
      <PasswordAuth
        onSuccess={() => setIsAuthenticated(true)}
        pageName="管理"
      />
    )
  }

  if (isLoading && !stats) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1>HEP-FIVE Audio Guide Analytics</h1>
        <div className="header-controls">
          <div className="time-range-selector">
            <label>Time Range:</label>
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(Number(e.target.value))}
            >
              <option value={1}>Last Hour</option>
              <option value={24}>Last 24 Hours</option>
              <option value={168}>Last Week</option>
              <option value={720}>Last Month</option>
              <option value={-1}>All Time (全ての期間)</option>
            </select>
          </div>
          
          <div className="auto-refresh-toggle">
            <label>
              <input 
                type="checkbox" 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto Refresh (30s)
            </label>
          </div>
          
          <button onClick={loadDashboardData} className="refresh-button">
            🔄 Refresh
          </button>
        </div>
      </div>

      {lastUpdated && (
        <div className="last-updated">
          Last updated: {lastUpdated.toLocaleString()}
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <div className="metric-value">{stats?.visitorsCount || 0}</div>
            <div className="metric-label">トップに訪れた数</div>
            {stats?.events && (
              <div className="device-breakdown">
                {(() => {
                  const breakdown = getDeviceBreakdown(stats.events, 'session_start')
                  return formatDeviceBreakdown(breakdown)
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">▶️</div>
          <div className="metric-content">
            <div className="metric-value">{stats?.playersCount || 0}</div>
            <div className="metric-label">再生した人の数</div>
            {stats?.events && (
              <div className="device-breakdown">
                {(() => {
                  const breakdown = getDeviceBreakdown(stats.events, 'audio_play_start')
                  return formatDeviceBreakdown(breakdown)
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">⏱️</div>
          <div className="metric-content">
            <div className="metric-value">{stats?.play3MinCount || 0}</div>
            <div className="metric-label">3分以上再生した人の数</div>
            {stats?.events && (
              <div className="device-breakdown">
                {(() => {
                  const breakdown = getDeviceBreakdown(stats.events, 'audio_milestone_3min')
                  return formatDeviceBreakdown(breakdown)
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <div className="metric-value">{stats?.completersCount || 0}</div>
            <div className="metric-label">最後まで再生した人の数</div>
            {stats?.events && (
              <div className="device-breakdown">
                {(() => {
                  const breakdown = getDeviceBreakdown(stats.events, 'completion_reached')
                  return formatDeviceBreakdown(breakdown)
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📝</div>
          <div className="metric-content">
            <div className="metric-value">{stats?.surveyRespondentsCount || 0}</div>
            <div className="metric-label">アンケートに答えた人の数</div>
            {stats?.events && (
              <div className="device-breakdown">
                {(() => {
                  const breakdown = getDeviceBreakdown(stats.events, 'survey_completed')
                  return formatDeviceBreakdown(breakdown)
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Survey Results */}
      {surveyBreakdown && (
        <div className="dashboard-section">
          <h2>Survey Results ({surveyBreakdown.total} total)</h2>
          <div className="survey-breakdown">
            <div className="survey-item excellent">
              <span className="survey-emoji">😍</span>
              <span className="survey-label">Excellent</span>
              <span className="survey-count">{surveyBreakdown.excellent}</span>
              <span className="survey-percentage">
                ({formatPercentage(getSurveyPercentage(surveyBreakdown.excellent, surveyBreakdown.total))})
              </span>
            </div>
            
            <div className="survey-item good">
              <span className="survey-emoji">😊</span>
              <span className="survey-label">Good</span>
              <span className="survey-count">{surveyBreakdown.good}</span>
              <span className="survey-percentage">
                ({formatPercentage(getSurveyPercentage(surveyBreakdown.good, surveyBreakdown.total))})
              </span>
            </div>
            
            <div className="survey-item fair">
              <span className="survey-emoji">😐</span>
              <span className="survey-label">Fair</span>
              <span className="survey-count">{surveyBreakdown.fair}</span>
              <span className="survey-percentage">
                ({formatPercentage(getSurveyPercentage(surveyBreakdown.fair, surveyBreakdown.total))})
              </span>
            </div>
            
            <div className="survey-item poor">
              <span className="survey-emoji">😞</span>
              <span className="survey-label">Poor</span>
              <span className="survey-count">{surveyBreakdown.poor}</span>
              <span className="survey-percentage">
                ({formatPercentage(getSurveyPercentage(surveyBreakdown.poor, surveyBreakdown.total))})
              </span>
            </div>
          </div>
        </div>
      )}



      {/* System Status */}
      <div className="dashboard-section">
        <h2>System Health</h2>
        <div className="health-indicators">
          <div className="health-item">
            <span className="health-label">Service Status</span>
            <span className="health-status online">🟢 Online</span>
          </div>
          
          <div className="health-item">
            <span className="health-label">Firebase Connection</span>
            <span className="health-status online">🟢 Connected</span>
          </div>
          
          <div className="health-item">
            <span className="health-label">Audio CDN</span>
            <span className="health-status online">🟢 Operational</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard